import { ANNOUNCE_EVENTS, ANNOUNCE_EVENT_INFO, ANNOUNCE_PLACEHOLDERS, type AnnounceSetupInput, type AnnounceView } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { errorMessage, get, post, put } from "../../api";
import { Alert, Badge, Button, Checkbox, Field, Loading, Panel, PanelHeader, formatDateTime } from "../../components/ui";

const EVENT_LABEL: Record<string, string> = { ...Object.fromEntries(ANNOUNCE_EVENTS.map((e) => [e, ANNOUNCE_EVENT_INFO[e].label])), test: "Test" };

/**
 * Clinic settings → Announcements: Vaulted says things out loud on the clinic's Echo devices through
 * Home Assistant (a new online booking, a mobile request, a declined membership payment). Any time of day.
 */
export function AnnounceTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["announcements"], queryFn: () => get<AnnounceView>("/api/announcements") });
  const [v, setV] = useState<AnnounceSetupInput | null>(null);
  const [testDevices, setTestDevices] = useState<string[]>([]);
  useEffect(() => {
    if (q.data && !v) setV({ webhook: "", clearWebhook: false, devices: q.data.devices, events: q.data.events });
  }, [q.data, v]);
  const save = useMutation({
    mutationFn: () => put<AnnounceView>("/api/announcements", v),
    onSuccess: (data) => {
      qc.setQueryData(["announcements"], data);
      setV((cur) => (cur ? { ...cur, webhook: "", clearWebhook: false } : cur));
    },
  });
  const test = useMutation({
    mutationFn: () => post<AnnounceView>("/api/announcements/test", { devices: testDevices }),
    onSuccess: (data) => qc.setQueryData(["announcements"], data),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["announcements"] }),
  });
  if (q.isLoading || !v) return q.error ? <Alert>{errorMessage(q.error)}</Alert> : <Loading />;
  const d = q.data!;
  const set = (patch: Partial<AnnounceSetupInput>) => setV({ ...v, ...patch });
  const setDevice = (i: number, patch: Partial<AnnounceSetupInput["devices"][number]>) => set({ devices: v.devices.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  const removeDevice = (i: number) => {
    const key = v.devices[i]!.key;
    set({
      devices: v.devices.filter((_, j) => j !== i),
      events: Object.fromEntries(ANNOUNCE_EVENTS.map((e) => [e, { ...v.events[e], devices: v.events[e].devices.filter((k) => k !== key) }])) as AnnounceSetupInput["events"],
    });
  };
  const setEvent = (e: (typeof ANNOUNCE_EVENTS)[number], patch: Partial<AnnounceSetupInput["events"][typeof e]>) => set({ events: { ...v.events, [e]: { ...v.events[e], ...patch } } });
  const toggle = (list: string[], key: string, on: boolean) => (on ? [...new Set([...list, key])] : list.filter((k) => k !== key));
  const saved = d.devices.filter((x) => x.key);

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Panel>
          <PanelHeader
            title="Home Assistant"
            description="Vaulted sends each announcement to your Home Assistant webhook, which says it on the Echo devices picked. Any time of day."
            action={d.hasWebhook ? <Badge tone="ok">Connected to {d.webhookHost}</Badge> : <Badge tone="off">Not set up</Badge>}
          />
          <div className="flex flex-col gap-3 px-5 py-4 text-sm">
            <Field
              label="Webhook address"
              type="password"
              autoComplete="off"
              placeholder={d.hasWebhook ? "Saved (paste a new one to replace)" : "https://…/api/webhook/…"}
              value={v.webhook}
              onChange={(e) => set({ webhook: e.target.value, clearWebhook: false })}
            />
            <p className="text-xs text-muted">
              It works like a password, so it's kept encrypted and never shown again. Paste it here only, never in an email, text, or chat. It must start with https:// so names aren't sent unencrypted.
            </p>
            {d.hasWebhook && (
              <div>
                <Checkbox label="Remove the saved webhook (stops all announcements)" checked={v.clearWebhook} onChange={(on) => set({ clearWebhook: on })} />
              </div>
            )}
          </div>
        </Panel>

        <Panel>
          <PanelHeader title="Devices" description="The Echo devices Vaulted can announce on, each with its name in your Home Assistant automation (e.g. clinic, kitchen). Only these can be picked below." />
          <div className="flex flex-col gap-2 px-5 py-4 text-sm">
            {v.devices.map((x, i) => (
              <div key={i} className="flex flex-wrap items-end gap-2">
                <Field label="Name" className="min-w-48 flex-1" placeholder="Clinic Echo Dot" value={x.label} onChange={(e) => setDevice(i, { label: e.target.value })} />
                <Field label="Name in Home Assistant" className="w-56" placeholder="clinic" value={x.key} onChange={(e) => setDevice(i, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })} />
                <Button type="button" variant="quiet" onClick={() => removeDevice(i)}>
                  Remove
                </Button>
              </div>
            ))}
            <div>
              <Button type="button" onClick={() => set({ devices: [...v.devices, { label: "", key: "" }] })}>
                + Add a device
              </Button>
            </div>
          </div>
        </Panel>

        <Panel>
          <PanelHeader title="What's announced" description="Each one on or off, where it's said, and the words. First names only unless you add more." />
          <div className="flex flex-col divide-y divide-mist px-5 text-sm">
            {ANNOUNCE_EVENTS.map((e) => {
              const s = v.events[e];
              const info = ANNOUNCE_EVENT_INFO[e];
              return (
                <div key={e} className="flex flex-col gap-2 py-4">
                  <Checkbox label={info.label} description={info.when} checked={s.on} onChange={(on) => setEvent(e, { on })} />
                  {s.on && (
                    <div className="flex flex-col gap-2 pl-7">
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        <span className="font-semibold text-ink-soft">On:</span>
                        {v.devices.filter((x) => x.key).length ? (
                          v.devices
                            .filter((x) => x.key)
                            .map((x) => <Checkbox key={x.key} label={x.label || x.key} checked={s.devices.includes(x.key)} onChange={(on) => setEvent(e, { devices: toggle(s.devices, x.key, on) })} />)
                        ) : (
                          <span className="text-muted">Add a device above first.</span>
                        )}
                      </div>
                      <Field label="Says" value={s.text} maxLength={300} onChange={(ev) => setEvent(e, { text: ev.target.value })} />
                      <p className="text-xs text-muted">{info.placeholders.map((p) => `{${p}} ${ANNOUNCE_PLACEHOLDERS[p]}`).join(" · ")}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Panel>

        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        {save.isSuccess && <Alert tone="ok">Saved.</Alert>}
        <div>
          <Button type="submit" variant="primary" busy={save.isPending}>
            Save
          </Button>
        </div>
      </form>

      {d.hasWebhook && saved.length > 0 && (
        <Panel>
          <PanelHeader title="Send a test" description="Says “Test announcement from Vaulted.” now, on the devices picked (saved devices only)." />
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-4 text-sm">
            {saved.map((x) => (
              <Checkbox key={x.key} label={x.label || x.key} checked={testDevices.includes(x.key)} onChange={(on) => setTestDevices(toggle(testDevices, x.key, on))} />
            ))}
            <Button disabled={!testDevices.length} busy={test.isPending} onClick={() => test.mutate()}>
              Send test
            </Button>
          </div>
          {test.error && (
            <div className="px-5 pb-4">
              <Alert>{errorMessage(test.error)}</Alert>
            </div>
          )}
          {test.isSuccess && (
            <div className="px-5 pb-4">
              <Alert tone="ok">Sent. If nothing was said, check the device names in Home Assistant.</Alert>
            </div>
          )}
        </Panel>
      )}

      <Panel>
        <PanelHeader title="Recent announcements" description="What Vaulted sent to Home Assistant, newest first." />
        {d.recent.length ? (
          <ul className="divide-y divide-mist px-5 text-sm">
            {d.recent.map((a) => (
              <li key={a.id} className="flex flex-wrap items-baseline gap-x-3 py-2">
                <span className="font-semibold">{EVENT_LABEL[a.event] ?? a.event}</span>
                <span className="min-w-0 flex-1">“{a.message}”</span>
                <span className="text-muted">{a.devices.map((k) => d.devices.find((x) => x.key === k)?.label ?? k).join(", ")}</span>
                <span className="text-muted">{formatDateTime(a.at)}</span>
                {a.status === "sent" ? <Badge tone="ok">Said</Badge> : a.status === "queued" ? <Badge tone="wait">Waiting</Badge> : <Badge tone="off">Not said</Badge>}
                {a.error && <span className="basis-full text-danger">{a.error}</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-5 py-3 text-sm text-muted">None yet.</p>
        )}
      </Panel>
    </div>
  );
}
